// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { Resources } from '@shared/localizations/Resources';
import { OperationType } from '@shared/utils/Trading/OperationType';
import { DataCache } from '../../DataCache';
import { OrderTif, OrderTifMap } from '@shared/utils/Trading/OrderTifEnum';
import { RulesSet } from '@shared/utils/Rules/RulesSet';
import { DateTimeUtils } from '@shared/utils/Time/DateTimeUtils';
import { DateTimeConvertor } from '@shared/utils/Time/DateTimeConvertor';
import { SLTPTrigger } from '@shared/utils/SlTpTrigger';
import { OrderStatus } from '@shared/utils/Trading/OrderStatus';
import { SlTpPriceType } from '@shared/utils/Enums/Constants';
import { OrderType } from '@shared/utils/Trading/OrderType';
import { InstrumentUtils } from '@shared/utils/Instruments/InstrumentUtils';
import { BaseSettings } from '../../Settings/BaseGeneralSettingsWrapper';
import { SlTpUtils } from '@shared/utils/Trading/SlTpUtils';
import { NO_BIND_TO } from '@shared/utils/Trading/OrderConstants';
class _OrderFormatter {
    OrderId(order) {
        return order.OrderNumber;
    }
    AccountStr(order) {
        var _a;
        return (_a = order.Account) === null || _a === void 0 ? void 0 : _a.toString();
    }
    SymbolStr(order) {
        var _a;
        return (_a = order.Instrument) === null || _a === void 0 ? void 0 : _a.DisplayName();
    }
    IsLong(order) {
        return order.BuySell === OperationType.Buy;
    }
    OrderType(order) {
        return order.OrderType;
    }
    OrderTypeStr(order, isShowSlOrTp = false) {
        const parentPosition = DataCache.Trading.getPositionById(order.BoundTo !== '-1' ? order.BoundTo : order.PositionId);
        if (!isNullOrUndefined(parentPosition) && order.isClosingOrder() && isShowSlOrTp) {
            if (parentPosition.SLOrder !== null &&
                parentPosition.SLOrder.OrderNumber === order.OrderNumber &&
                parentPosition.SLOrder.OrderType === OrderType.TrailingStop) {
                return Resources.getResource('Web.mobile.active.Order.TrStopLoss');
            }
            else if (parentPosition.SLOrder !== null && parentPosition.SLOrder.OrderNumber === order.OrderNumber) {
                return Resources.getResource('Web.mobile.active.Order.StopLoss');
            }
            else if (parentPosition.TPOrder !== null && parentPosition.TPOrder.OrderNumber === order.OrderNumber) {
                return Resources.getResource('Web.mobile.active.Order.TakeProfit');
            }
        }
        else {
            const orderParameterContainer = DataCache.OrderParameterContainer;
            const orderTypeObj = orderParameterContainer.GetOrderType(order.OrderType);
            return Resources.getResource('property.' + orderTypeObj.localizationKey());
        }
    }
    Operation(order) {
        return order.BuySell;
    }
    OperationStr(order) {
        return order.GetFullOperationName(true);
    }
    ValidityStr(order) {
        return OrderFormatter.GetFormattedValidityStr(order.TimeInForce, order.ExpireAt);
    }
    GetFormattedValidityStr(validity, date) {
        let tif = Resources.getResource('property.' + OrderTifMap[validity]);
        if (validity === OrderTif.GTD) {
            const dtString = DataCache.isAllowedForMyUser(RulesSet.FUNCTION_SHOW_TIME_FOR_GTD)
                ? DateTimeUtils.formatDate(date, 'DD.MM.YYYY')
                : date.toLocaleDateString();
            tif += ' (' + dtString + ')';
        }
        return tif;
    }
    Amount(order) {
        const amount = order.Amount;
        if (BaseSettings.isQuantityInLots || order.Instrument === null) {
            return amount;
        }
        else {
            return amount * order.Instrument.LotSize;
        }
    }
    AmountStr(order) {
        const amount = this.Amount(order);
        return this.FormatAmount(order, amount);
    }
    FormatAmount(order, amount) {
        return DataCache.formatVolume(order.Instrument, Math.abs(amount), BaseSettings.isQuantityInLots, order.ProductType, order.Account);
    }
    Price(order) {
        return order.Price;
    }
    PriceStr(order) {
        var _a;
        return (_a = order.Instrument) === null || _a === void 0 ? void 0 : _a.formatPrice(this.Price(order));
    }
    StopPrice(order) {
        return order.StopLimit;
    }
    StopPriceStr(order) {
        var _a;
        return (_a = order.Instrument) === null || _a === void 0 ? void 0 : _a.formatPrice(this.StopPrice(order));
    }
    CurrentPrice(order) {
        return order.CurPriceOpen || order.CurPriceClose;
    }
    CurrentPriceStr(order) {
        var _a;
        return (_a = order.Instrument) === null || _a === void 0 ? void 0 : _a.formatPrice(this.CurrentPrice(order));
    }
    IsSlOrTp(order) {
        return order.PositionId !== '-1';
    }
    IsStopLossInOffset(order) {
        return order.StopLossPriceType !== SlTpPriceType.Absolute;
    }
    IsStopLossTrailingStop(order) {
        return order.StopLossPriceType === SlTpPriceType.TrOffset;
    }
    HasStopLossPrice(order) {
        return !isNaN(order.StopLossPriceValue);
    }
    StopLossPrice(order) {
        return order.GetStopLossInPriceValue();
    }
    StopLossPriceStr(order, defaultStr = '') {
        var _a;
        const stopLossPrice = this.StopLossPrice(order);
        if (isNaN(stopLossPrice)) {
            return defaultStr;
        }
        const stopLossPriceStr = (_a = order.Instrument) === null || _a === void 0 ? void 0 : _a.formatPrice(stopLossPrice);
        const trigger = order.TriggerSL || order.TriggerSLTP;
        if (trigger) {
            return stopLossPriceStr + ' ' + SLTPTrigger.ConvertBidAskRawValueToStr(trigger);
        }
        else {
            return stopLossPriceStr;
        }
    }
    StopLossOffset(order) {
        return order.GetStopLossInOffsetValue();
    }
    StopLossOffsetStr(order, defaultStr = '') {
        var _a;
        const sltpHolder = order.createSlTpHolder();
        if (sltpHolder.isSlEmpty()) {
            return defaultStr;
        }
        const realOffset = this.StopLossOffset(order);
        const visualOffsetType = (_a = SlTpUtils.getSLVisualOffsetType(order.Instrument, sltpHolder)) !== null && _a !== void 0 ? _a : SlTpUtils.getDefaultVisualOffsetType(order.Instrument);
        const stopLossVisualOffset = SlTpUtils.toVisualValue(realOffset, order.Instrument, visualOffsetType);
        const stopLossVisualOffsetStr = SlTpUtils.formatSLTPValue(stopLossVisualOffset, order.Instrument, visualOffsetType);
        const trigger = order.TriggerSL || order.TriggerSLTP;
        if (trigger) {
            return stopLossVisualOffsetStr + ' ' + SLTPTrigger.ConvertBidAskRawValueToStr(trigger);
        }
        else {
            return stopLossVisualOffsetStr;
        }
    }
    IsTakeProfitInOffset(order) {
        return order.TakeProfitPriceType !== SlTpPriceType.Absolute;
    }
    HasTakeProfitPrice(order) {
        return !isNaN(order.TakeProfitPriceValue);
    }
    TakeProfitPrice(order) {
        return order.GetTakeProfitInPriceValue();
    }
    TakeProfitPriceStr(order, defaultStr = '') {
        var _a;
        const takeProfitPrice = this.TakeProfitPrice(order);
        if (isNaN(takeProfitPrice)) {
            return defaultStr;
        }
        const takeProfitPriceStr = (_a = order.Instrument) === null || _a === void 0 ? void 0 : _a.formatPrice(takeProfitPrice);
        const trigger = order.TriggerTP || order.TriggerSLTP;
        if (trigger) {
            return takeProfitPriceStr + ' ' + SLTPTrigger.ConvertBidAskRawValueToStr(trigger);
        }
        else {
            return takeProfitPriceStr;
        }
    }
    TakeProfitOffset(order) {
        return order.GetTakeProfitInOffsetValue();
    }
    TakeProfitOffsetStr(order, defaultStr = '') {
        var _a;
        const sltpHolder = order.createSlTpHolder();
        if (sltpHolder.isTpEmpty()) {
            return defaultStr;
        }
        const realOffset = this.TakeProfitOffset(order);
        const visualOffsetType = (_a = SlTpUtils.getTPVisualOffsetType(order.Instrument, sltpHolder)) !== null && _a !== void 0 ? _a : SlTpUtils.getDefaultVisualOffsetType(order.Instrument);
        const takeProfitVisualOffset = SlTpUtils.toVisualValue(realOffset, order.Instrument, visualOffsetType);
        const takeProfitVisualOffsetStr = SlTpUtils.formatSLTPValue(takeProfitVisualOffset, order.Instrument, visualOffsetType);
        const trigger = order.TriggerTP || order.TriggerSLTP;
        if (trigger) {
            return takeProfitVisualOffsetStr + ' ' + SLTPTrigger.ConvertBidAskRawValueToStr(trigger);
        }
        else {
            return takeProfitVisualOffsetStr;
        }
    }
    AverageFilledPrice(order) {
        return isNullOrUndefined(order) ? 0 : order.AverageFilledPrice;
    }
    AverageFilledPriceStr(order) {
        var _a;
        return (_a = order.Instrument) === null || _a === void 0 ? void 0 : _a.formatPrice(this.AverageFilledPrice(order));
    }
    QuantityFilled(order) {
        const amountFilled = order.SharesFilled;
        if (BaseSettings.isQuantityInLots || order.Instrument === null) {
            return amountFilled;
        }
        else {
            return amountFilled * order.Instrument.LotSize;
        }
    }
    QuantityFilledStr(order) {
        const quantityFilledValue = this.QuantityFilled(order);
        return DataCache.formatVolume(order.Instrument, Math.abs(quantityFilledValue), BaseSettings.isQuantityInLots, order.ProductType, order.Account);
    }
    QuantityRemaining(order) {
        const amountRemaining = order.AmountRemaining;
        if (BaseSettings.isQuantityInLots || order.Instrument === null) {
            return amountRemaining;
        }
        else {
            return amountRemaining * order.Instrument.LotSize;
        }
    }
    QuantityRemainingStr(order) {
        const quantityRemainingValue = this.QuantityRemaining(order);
        return DataCache.formatVolume(order.Instrument, Math.abs(quantityRemainingValue), BaseSettings.isQuantityInLots, order.ProductType, order.Account);
    }
    BoundToStr(order, defaultStr = '') {
        if (order.BoundTo !== NO_BIND_TO) {
            return order.BoundTo;
        }
        else if (order.PositionId !== NO_BIND_TO) {
            return order.PositionId;
        }
        else {
            return defaultStr;
        }
    }
    IsOrderLinked(order) {
        return order.BoundTo !== NO_BIND_TO;
    }
    DateTime(order) {
        return DateTimeConvertor.ConvertUTCTimeToSelectedTimeZone(order.UTCDateTime);
    }
    DateTimeStr(order) {
        const dateTime = this.DateTime(order);
        return DateTimeUtils.formatDate(dateTime, 'DD.MM.YYYY  HH:mm:ss');
    }
    OrderStatus(order) {
        return order.Status;
    }
    OrderStatusStr(order) {
        return this.localizeOrderStatus(this.OrderStatus(order));
    }
    ProductType(order) {
        return order.ProductType;
    }
    ProductTypeStr(order) {
        return InstrumentUtils.GetLocalizedProductType(order.Instrument, order.ProductType);
    }
    TradingExchangeStr(order) {
        var _a;
        return (_a = order.Instrument) === null || _a === void 0 ? void 0 : _a.TradingExchange;
    }
    CommentStr(order) {
        return order.GetComment();
    }
    localizeOrderStatus(status) {
        let key = 'UNKNOWN';
        switch (status) {
            case OrderStatus.UNKNOWN:
                key = 'UNKNOWN';
                break;
            case OrderStatus.REPLACED:
                key = 'REPLACED';
                break;
            case OrderStatus.PENDING_NEW:
                key = 'PENDING_NEW';
                break;
            case OrderStatus.PENDING_EXECUTION:
                key = 'PENDING_EXECUTION';
                break;
            case OrderStatus.PENDING_CANCEL:
                key = 'PENDING_CANCEL';
                break;
            case OrderStatus.PENDING_REPLACE:
                key = 'PENDING_REPLACE';
                break;
            case OrderStatus.PENDING_REPLACE_NOT_ACTIVE:
                key = 'PENDING_REPLACE_NOT_ACTIVE';
                break;
            case OrderStatus.NEW:
                key = 'NEW';
                break;
            case OrderStatus.ACCEPTED:
                key = 'ACCEPTED';
                break;
            case OrderStatus.PART_FILLED:
                key = 'PART_FILLED';
                break;
            case OrderStatus.FILLED:
                key = 'FILLED';
                break;
            case OrderStatus.CANCELED:
                key = 'CANCELED';
                break;
            case OrderStatus.REFUSED:
                key = 'REFUSED';
                break;
            case OrderStatus.WAITING_MARKET:
                key = 'WAITING_MARKET';
                break;
            case OrderStatus.WAITING_CANCELLATION:
                key = 'WAITING_CANCELLATION';
                break;
            case OrderStatus.OFF_MARKET:
                key = 'OFF_MARKET';
                break;
            case OrderStatus.UNPLACED:
                key = 'UNPLACED';
                break;
        }
        return Resources.getResource('Order.OrderStatus.STATUS_' + key);
    }
}
export const OrderFormatter = new _OrderFormatter();
